import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const LS_KEY = 'jpc.displayTimeZone';

// Curated short list — the realistic operator set. The viewer's own zone is
// always added on top (deduped) so "my local time" is one click away.
const CURATED_ZONES = ['UTC', 'America/Chicago', 'America/New_York', 'Europe/London'];

/** The browser's resolved IANA zone, or 'UTC' if it can't be determined. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const FRIENDLY: Record<string, string> = {
  UTC: 'UTC',
  'America/Chicago': 'Central',
  'America/New_York': 'Eastern',
  'Europe/London': 'London',
};

/** A short human label for a zone — "Central", "UTC", or the city for any other IANA id. */
export function zoneLabel(tz: string): string {
  return FRIENDLY[tz] ?? tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
}

/** Current short abbreviation for a zone (e.g. "CDT", "GMT"), or '' if unavailable. */
export function zoneAbbrev(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

function readStored(): string {
  if (typeof window === 'undefined') return browserTimeZone();
  try {
    return window.localStorage.getItem(LS_KEY) || browserTimeZone();
  } catch {
    return browserTimeZone();
  }
}

type Ctx = {
  /** Active IANA display zone — what every `FormatDate` instant renders in. */
  timeZone: string;
  setTimeZone: (tz: string) => void;
  /** Curated zones + viewer's own + whatever's currently selected, deduped. */
  options: string[];
};

const TimeZoneContext = createContext<Ctx | null>(null);

export function TimeZoneProvider({ children }: { children: ReactNode }) {
  const [timeZone, setTimeZoneState] = useState<string>(readStored);

  const setTimeZone = useCallback((tz: string) => {
    setTimeZoneState(tz);
    try {
      window.localStorage.setItem(LS_KEY, tz);
    } catch {
      /* private mode / storage disabled — selection just won't persist */
    }
  }, []);

  const value = useMemo<Ctx>(() => {
    const options = Array.from(new Set([browserTimeZone(), ...CURATED_ZONES, timeZone]));
    return { timeZone, setTimeZone, options };
  }, [timeZone, setTimeZone]);

  return <TimeZoneContext.Provider value={value}>{children}</TimeZoneContext.Provider>;
}

/**
 * The active display zone. Non-throwing: `FormatDate` is a low-level atom that
 * may render outside the provider (isolated component tests, the sign-in
 * screen), so this falls back to the browser zone rather than throwing.
 */
export function useDisplayTimeZone(): string {
  return useContext(TimeZoneContext)?.timeZone ?? browserTimeZone();
}

/** Selector control — must be used inside the provider (it mutates state). */
export function useTimeZoneControl(): Ctx {
  const ctx = useContext(TimeZoneContext);
  if (!ctx) throw new Error('useTimeZoneControl must be used within TimeZoneProvider');
  return ctx;
}
